'use client';

import React, { useEffect, useRef } from 'react';

interface Props {
  onLoadMore: () => void;
  hasMore: boolean;
}

/**
 * Infinite-scroll sentinel for the SSR grid. All tiles are already in the SSR HTML (for SEO); this
 * just reveals the next batch (via onLoadMore) as the sentinel scrolls into view — no refetch. A
 * stable IntersectionObserver (re-created only when `hasMore` flips) reveals ~one batch per scroll,
 * with a rootMargin so the next batch is ready slightly before the user reaches the end.
 */
const SsrInfiniteScroll: React.FC<Props> = ({ onLoadMore, hasMore }) => {
  const ref = useRef<HTMLDivElement>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  useEffect(() => {
    if (!hasMore) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onLoadMoreRef.current();
      },
      { rootMargin: '400px 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore]);

  if (!hasMore) return null;
  return <div ref={ref} aria-hidden className="h-px w-full" />;
};

export default SsrInfiniteScroll;
